import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import KPIBubbles from "../components/KPIBubbles";
import DataVisualizer from "../components/DataVisualizer";
import CreativeGallery from "../components/CreativeGallery";
import SmsEmailDashboard from "../components/SmsEmailDashboard";
const SharedContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px 20px;
`;

const Header = styled.header`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 28px;
  color: #333;
  margin-bottom: 10px;
`;

const SharedBanner = styled.div`
  background-color: #f8f9fa;
  border-left: 4px solid #6ed216;
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 0 5px 5px 0;
  font-size: 14px;
  color: #666;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 18px;
  color: #666;
`;

const ErrorContainer = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 15px;
  border-radius: 5px;
  text-align: center;
  margin-bottom: 20px;
`;

// Ajout d'un style pour le badge de filtre
const FilterBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #e8f0fe;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 14px;
  color: #1a73e8;
  margin-top: 10px;
`;

// Ajouter un badge pour afficher le taux de conversion
const ConversionRateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #fef7e0;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 14px;
  color: #b06000;
  margin-top: 10px;
  margin-left: 10px;
`;

function SharedReportPage() {
  const { token } = useParams();
  const [reportData, setReportData] = useState(null);
  const [processedData, setProcessedData] = useState([]);
  const [selectedKPI, setSelectedKPI] = useState("impressions");
  const [selectedFilter, setSelectedFilter] = useState("day");
  const [insertionOrderId, setInsertionOrderId] = useState("all"); // Ajout de l'état pour stocker l'insertionOrderId
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const apiUrl =
    process.env.REACT_APP_API_URL || "https://report-campaign.sirdata.io";

  useEffect(() => {
    const fetchSharedReport = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiUrl}/api/share/${token}`);

        if (response.data.success) {
          const report = response.data.report;

          // Récupérer l'insertionOrderId et le taux de conversion
          if (response.data.insertionOrderId) {
            setInsertionOrderId(response.data.insertionOrderId);
          }

          // Récupérer le taux de conversion
          const convRate = response.data.conversionRate || 1;

          // Vérifier la structure des données
          if (report.data && report.data.length > 0) {
            // Traiter les données pour les rendre compatibles avec les composants
            const processed = report.data.map((item) => {
              // Extraire les valeurs de base avec des valeurs par défaut
              const impressions = Number(item.impressions || item.imps || 0);
              const clicks = Number(item.clicks || 0);
              const viewableImpressions = Number(item.viewableImpressions || 0);
              const budget = Number(item.budget || item.cost || 0);
              const conversions = Number(
                item.conversions || item.total_convs || 0
              );

              // Appliquer le taux de conversion aux métriques pertinentes
              const convertedBudget = budget * convRate;
              const convertedImpressions = impressions;
              const convertedClicks = clicks;
              const convertedConversions = conversions;
              const convertedViewableImpressions = viewableImpressions;

              // Calculer le CTR avec les valeurs converties
              const calculatedCTR =
                convertedImpressions > 0
                  ? (convertedClicks / convertedImpressions) * 100
                  : 0;

              // Calculer la visibilité avec les valeurs converties
              let calculatedViewability = 0;
              if (
                convertedImpressions > 0 &&
                convertedViewableImpressions > 0
              ) {
                calculatedViewability =
                  (convertedViewableImpressions / convertedImpressions) * 100;
              }

              const processedItem = {
                date: item.date,
                creativeId: item.creativeId || item.creative_id || "",
                creativeName: item.creativeName || item.creative_name || "",
                creative:
                  item.creativeName ||
                  item.creative_name ||
                  `Creative ${
                    item.creativeId || item.creative_id || "Unknown"
                  }`,
                size: item.size || "",
                impressions: convertedImpressions,
                clicks: convertedClicks,
                ctr: calculatedCTR,
                conversions: convertedConversions,
                budget: convertedBudget,
                originalBudget: budget, // Conserver le budget original pour référence
                originalImpressions: impressions, // Conserver les impressions originales
                originalClicks: clicks, // Conserver les clics originaux
                originalConversions: conversions, // Conserver les conversions originales
                viewableImpressions: convertedViewableImpressions,
                measurableImpressions:
                  Number(item.measurableImpressions || 0) * convRate,
                viewability: calculatedViewability,
                insertionOrderId: item.insertionOrderId || null,
                insertionOrderName: item.insertionOrderName || "Inconnu",
              };
              return processedItem;
            });
            setProcessedData(processed);
          } else {
            console.warn(
              "Aucune donnée dans le rapport ou structure incorrecte"
            );
          }

          setReportData(report);
        } else {
          setError("Erreur lors de la récupération des données du rapport");
        }
      } catch (err) {
        setError(`Erreur: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchSharedReport();
  }, [token, apiUrl]);
  // Filtrer les données en fonction de l'insertionOrderId
  const filteredData = useMemo(() => {
    if (!processedData || processedData.length === 0) return [];

    if (insertionOrderId === "all") {
      return processedData;
    }

    return processedData.filter(
      (item) => item.insertionOrderId === insertionOrderId
    );
  }, [processedData, insertionOrderId]);

  // Extraire les insertion orders uniques pour l'affichage du nom
  const insertionOrders = useMemo(() => {
    if (!processedData || processedData.length === 0) return [];

    const uniqueOrders = [
      ...new Set(
        processedData
          .filter((item) => item.insertionOrderId && item.insertionOrderName)
          .map((item) =>
            JSON.stringify({
              id: item.insertionOrderId,
              name: item.insertionOrderName,
            })
          )
      ),
    ].map((item) => JSON.parse(item));

    return uniqueOrders;
  }, [processedData]);

  // Obtenir le nom de l'insertion order pour l'affichage
  const getInsertionOrderName = (id) => {
    const order = insertionOrders.find((order) => order.id === id);
    return order ? order.name : "Inconnu";
  };

  const handleKPISelect = (kpi) => {
    setSelectedKPI(kpi);
  };

  return (
    <SharedContainer>
      {error && <ErrorContainer>{error}</ErrorContainer>}

      {loading ? (
        <LoadingContainer>
          Chargement des données du rapport...
        </LoadingContainer>
      ) : reportData ? (
        <>
          <Header>
            <Title>{reportData.campaignName || "Rapport de campagne"}</Title>
            <SharedBanner>
              Ce rapport a été partagé avec vous. Vous visualisez une version en
              lecture seule.
              {insertionOrderId !== "all" && (
                <FilterBadge>
                  Filtré par Insertion Order:{" "}
                  {getInsertionOrderName(insertionOrderId)}
                </FilterBadge>
              )}
            </SharedBanner>
          </Header>
          {filteredData.length > 0 && reportData?.source !== "sms-email" ? (
            <>
              <KPIBubbles
                data={filteredData}
                onKPISelect={handleKPISelect}
                selectedKPI={selectedKPI}
              />
              <DataVisualizer
                data={filteredData}
                selectedKPI={selectedKPI}
                selectedFilter={selectedFilter || "day"}
                onFilterChange={(filter) => setSelectedFilter(filter)}
              />
              <CreativeGallery data={filteredData} selectedKPI={selectedKPI} />
            </>
          ) : reportData?.source === "sms-email" ? (
            <SmsEmailDashboard reportData={reportData} />
          ) : (
            <ErrorContainer>
              Aucune donnée disponible dans ce rapport
            </ErrorContainer>
          )}
        </>
      ) : (
        <ErrorContainer>Rapport non trouvé ou lien expiré</ErrorContainer>
      )}
    </SharedContainer>
  );
}

export default SharedReportPage;
